"use client";
import React from "react";
import {
  Lock,
  Shield,
  Check,
  Database,
  Globe,
  Cookie,
  Server,
  Clock,
  UserCheck,
  AlertTriangle,
  CreditCard,
  Receipt,
  Info,
} from "lucide-react";

const SECTIONS: { id: string; title: string }[] = [
  { id: "introducere", title: "Introducere" },
  { id: "definitii", title: "Definiții" },
  { id: "categorii-date", title: "Categorii de Date Prelucrate" },
  { id: "cum-colectam", title: "Cum Colectăm Datele" },
  { id: "scop", title: "Scopul și Temeiul Prelucrării" },
  { id: "dezvaluire", title: "Destinatarii Datelor" },
  { id: "transfer", title: "Transferul Datelor în Afara UE/SEE" },
  { id: "stocare", title: "Perioada de Stocare" },
  { id: "cookies", title: "Cookie-uri și Tehnologii Similare" },
  { id: "drepturi", title: "Drepturile Dumneavoastră" },
  { id: "securitate", title: "Securitatea Datelor" },
  { id: "brese", title: "Breșele de Securitate" },
  { id: "decizii-automate", title: "Decizii Automate și Profilare" },
  { id: "minori", title: "Datele Minorilor" },
  { id: "modificari", title: "Modificări ale Politicii" },
  { id: "contact", title: "Informații de Contact" },
];

const RECIPIENTS: { name: string; role: string; location: string }[] = [
  {
    name: "Stripe Payments Europe, Ltd. și Stripe, Inc.",
    role: "Procesarea plăților cu cardul pentru abonamentele proprietarilor de locații, stocarea securizată a cardului și determinarea TVA-ului aplicabil.",
    location: "Irlanda / Statele Unite ale Americii",
  },
  {
    name: "Oblio Software S.R.L.",
    role: "Emiterea și gestionarea facturilor pentru abonamente.",
    location: "România",
  },
  {
    name: "Twilio",
    role: "Transmiterea codurilor SMS pentru verificarea numărului de telefon.",
    location: "Irlanda / Statele Unite ale Americii",
  },
  {
    name: "Hetzner Online GmbH",
    role: "Găzduirea aplicației, a bazelor de date și stocarea fotografiilor încărcate (centru de date Nürnberg).",
    location: "Germania",
  },
  {
    name: "Porkbun LLC",
    role: "Serviciul de e-mail prin care transmitem mesajele tranzacționale (verificare cont, resetare parolă, notificări, facturi).",
    location: "Statele Unite ale Americii",
  },
  {
    name: "HERE Global B.V.",
    role: "Servicii de hărți și de identificare a adreselor locațiilor sportive.",
    location: "Țările de Jos",
  },
  {
    name: "Google Ireland Limited",
    role: "Autentificarea cu contul Google, dacă alegeți această metodă de conectare.",
    location: "Irlanda",
  },
];

const PURPOSES: { purpose: string; basis: string }[] = [
  {
    purpose:
      "Crearea și administrarea contului, autentificarea și furnizarea funcționalităților Platformei",
    basis: "Executarea contractului — art. 6 alin. (1) lit. b) GDPR",
  },
  {
    purpose:
      "Efectuarea, confirmarea și gestionarea rezervărilor, inclusiv comunicarea datelor necesare proprietarului locației",
    basis: "Executarea contractului — art. 6 alin. (1) lit. b) GDPR",
  },
  {
    purpose:
      "Verificarea numărului de telefon și prevenirea rezervărilor frauduloase",
    basis:
      "Executarea contractului și interesul legitim — art. 6 alin. (1) lit. b) și f) GDPR",
  },
  {
    purpose:
      "Gestionarea abonamentelor proprietarilor de locații, procesarea plăților recurente și prevenirea fraudei la plată",
    basis:
      "Executarea contractului și interesul legitim — art. 6 alin. (1) lit. b) și f) GDPR",
  },
  {
    purpose:
      "Emiterea facturilor, evidența contabilă, raportările fiscale (inclusiv RO e-Factura) și arhivarea documentelor",
    basis: "Obligație legală — art. 6 alin. (1) lit. c) GDPR",
  },
  {
    purpose:
      "Securitatea Platformei, jurnalele tehnice, prevenirea abuzurilor și îmbunătățirea serviciilor",
    basis: "Interesul legitim — art. 6 alin. (1) lit. f) GDPR",
  },
  {
    purpose:
      "Soluționarea reclamațiilor, apărarea drepturilor în justiție și răspunsul la solicitările autorităților",
    basis:
      "Interesul legitim și obligația legală — art. 6 alin. (1) lit. f) și c) GDPR",
  },
  {
    purpose: "Comunicări de marketing și noutăți despre Platformă",
    basis: "Consimțământul dumneavoastră — art. 6 alin. (1) lit. a) GDPR",
  },
];

const RETENTION: { what: string; how_long: string }[] = [
  {
    what: "Datele contului și profilului",
    how_long:
      "Pe durata existenței contului. După ștergerea contului, sunt eliminate din sistemele active în cel mult 30 de zile, cu excepția datelor pe care trebuie să le păstrăm în temeiul unei obligații legale.",
  },
  {
    what: "Datele privind rezervările",
    how_long:
      "Pe durata necesară procesării și ulterior pentru apărarea drepturilor, dar nu mai mult de 5 ani.",
  },
  {
    what: "Datele de facturare ale locației (denumire, cod fiscal, adresă)",
    how_long:
      "Pe durata abonamentului și ulterior pe perioada de arhivare a documentelor justificative aferente.",
  },
  {
    what: "Facturile și documentele contabile aferente abonamentelor",
    how_long:
      "Conform Legii contabilității nr. 82/1991 și normelor de aplicare: 5 ani pentru documentele justificative, respectiv 10 ani pentru registrele contabile și situațiile financiare.",
  },
  {
    what: "Evidența plăților (sumă, TVA, referința tranzacției, ultimele cifre ale cardului)",
    how_long:
      "Aceeași perioadă ca documentele contabile pe care le justifică.",
  },
  {
    what: "CNP-ul, dacă alegeți să îl furnizați",
    how_long:
      "Doar pe durata arhivării facturii în care apare. Nu îl folosim în niciun alt scop.",
  },
  {
    what: "Jurnalele tehnice și de securitate",
    how_long: "De regulă, maximum 12 luni.",
  },
  {
    what: "Datele colectate prin cookie-uri",
    how_long:
      "Conform duratelor din secțiunea 9, maximum 12 luni de la ultima utilizare.",
  },
];

const COOKIES: { name: string; purpose: string; duration: string }[] = [
  {
    name: "Cookie-uri de autentificare (token de sesiune și de reîmprospătare)",
    purpose:
      "Vă mențin conectat între vizite și protejează sesiunea împotriva utilizării neautorizate. Fără ele, Platforma nu poate funcționa.",
    duration: "Sesiune, respectiv până la 12 luni",
  },
  {
    name: "Cookie de preferință a limbii",
    purpose: "Reține limba în care alegeți să vizualizați Platforma.",
    duration: "Până la 12 luni",
  },
];

const PrivacyView = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      <main className="container mx-auto px-4 py-12 pt-24">
        <div className="max-w-4xl mx-auto bg-gray-800/50 rounded-xl border border-gray-700/50 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="p-8 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-gray-700/50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Lock className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Politica de Confidențialitate Atlivo
                </h1>
                <p className="text-gray-300">
                  Versiunea 2.0 · Ultima actualizare: 27 Iulie 2026
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8 text-gray-300">
            <p>
              Confidențialitatea datelor dumneavoastră cu caracter personal este
              o prioritate pentru SEERSTECH SRL, operatorul Platformei Atlivo,
              cu sediul în Sălaj, Municipiul Zalău, Strada AVRAM IANCU, Nr. 39,
              Bloc N7, Ap. 7, înregistrată la Registrul Comerțului sub nr.
              J31/131/15.02.2023, cod fiscal 47639966, e-mail:
              support@atlivo.com, (denumită în continuare „Operatorul" sau
              „Noi").
            </p>

            <p>
              Această Politică de Confidențialitate descrie modul în care
              colectăm, utilizăm, stocăm și protejăm datele dumneavoastră cu
              caracter personal în conformitate cu Regulamentul (UE) 2016/679
              (GDPR), cu Legea nr. 190/2018 și cu restul legislației românești
              aplicabile.
            </p>

            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <p>
                  <span className="font-semibold text-white">
                    Ce este nou în versiunea 2.0:
                  </span>{" "}
                  odată cu introducerea abonamentelor pentru proprietarii de
                  locații, am adăugat datele de facturare și de plată,{" "}
                  <a href="#dezvaluire" className="text-blue-400 underline">
                    lista completă a furnizorilor
                  </a>{" "}
                  cu care lucrăm,{" "}
                  <a href="#transfer" className="text-blue-400 underline">
                    informații corecte despre transferurile în afara UE
                  </a>
                  , o secțiune dedicată{" "}
                  <a href="#cookies" className="text-blue-400 underline">
                    cookie-urilor
                  </a>{" "}
                  și tabele detaliate privind temeiurile și duratele de stocare.
                </p>
              </div>
            </div>

            <div className="bg-gray-700/30 p-6 rounded-xl">
              <h2 className="text-xl font-bold text-white mb-4">Cuprins</h2>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                {SECTIONS.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex gap-2 items-center text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <span className="bg-blue-500/20 text-blue-400 w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                      {index + 1}
                    </span>
                    <span>{section.title}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Sections */}
            <section id="introducere" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                1. Introducere
              </h2>
              <div className="space-y-4">
                <p>
                  Această politică explică ce date colectăm, de ce le colectăm
                  și ce facem cu ele atunci când utilizați Platforma Atlivo. Ne
                  angajăm să prelucrăm datele dumneavoastră cu transparență și
                  securitate.
                </p>

                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    1.1. Operatorul de Date
                  </h3>
                  <p>
                    Operatorul de date cu caracter personal este SEERSTECH SRL,
                    responsabil pentru prelucrarea datelor furnizate prin
                    Platforma Atlivo.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    1.2. Cui se Aplică Această Politică?
                  </h3>
                  <p>
                    Această politică se aplică tuturor utilizatorilor și
                    vizitatorilor Platformei Atlivo, inclusiv sportivilor,
                    proprietarilor de locații sportive și personalului acestora.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    1.3. Proprietarii de Locații ca Operatori Independenți
                  </h3>
                  <p>
                    Pentru a putea onora rezervările, proprietarii de locații
                    primesc datele necesare ale sportivilor. În privința acestor
                    date, proprietarul locației acționează ca operator
                    independent și răspunde de propria conformitate cu GDPR.
                    Prezenta politică descrie prelucrările efectuate de Atlivo,
                    nu pe cele efectuate ulterior de proprietarul locației.
                  </p>
                </div>
              </div>
            </section>

            <section id="definitii" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                2. Definiții
              </h2>
              <ul className="space-y-2 list-inside">
                <li>
                  <span className="font-semibold text-white">
                    Date cu caracter personal
                  </span>
                  : Informații privind o persoană fizică identificată sau
                  identificabilă (ex. nume, e-mail, telefon).
                </li>
                <li>
                  <span className="font-semibold text-white">Prelucrare</span>:
                  Orice operațiune asupra datelor (ex. colectare, stocare,
                  utilizare, ștergere).
                </li>
                <li>
                  <span className="font-semibold text-white">Operator</span>:
                  Entitatea care stabilește scopurile și mijloacele prelucrării
                  datelor.
                </li>
                <li>
                  <span className="font-semibold text-white">Împuternicit</span>
                  : Furnizorul care prelucrează datele în numele și după
                  instrucțiunile Operatorului (ex. furnizorul de găzduire).
                </li>
                <li>
                  <span className="font-semibold text-white">Utilizator</span>:
                  Persoana care accesează sau utilizează Platforma Atlivo.
                </li>
              </ul>
            </section>

            <section id="categorii-date" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                3. Categorii de Date Prelucrate
              </h2>
              <p className="mb-4">Colectăm următoarele categorii de date:</p>
              <ul className="space-y-3 list-inside">
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold text-white">
                      Date de identificare și de contact
                    </span>
                    : nume, prenume sau nickname, adresă de e-mail, număr de
                    telefon, rolul ales (sportiv sau proprietar de locație).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold text-white">
                      Date despre rezervări
                    </span>
                    : locațiile și terenurile rezervate, intervalele orare,
                    rezervările recurente, prețul afișat, precum și numele
                    persoanei care a operat rezervarea (jurnalul de activitate
                    al locației).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Receipt className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold text-white">
                      Date de facturare (doar pentru proprietarii de locații)
                    </span>
                    : denumirea legală sau numele complet, calitatea de persoană
                    fizică sau juridică, CUI/CIF, numărul de la Registrul
                    Comerțului, calitatea de plătitor de TVA, adresa completă
                    (stradă, oraș, județ, cod poștal, țară) și adresa de e-mail
                    pentru facturi. Opțional, CNP-ul — a se vedea mai jos.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold text-white">
                      Date privind plățile și abonamentul
                    </span>
                    : starea abonamentului, planul și prețul, perioada de probă
                    și perioadele de facturare, istoricul plăților (sumă, TVA,
                    dată, rezultat), seria, numărul și link-ul facturii, precum
                    și identificatorii tehnici atribuiți de procesatorul de
                    plăți. Din datele cardului păstrăm exclusiv tipul cardului,
                    ultimele cifre și data expirării.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold text-white">
                      Date tehnice
                    </span>
                    : adresa IP, tipul de dispozitiv, browserul, jurnalele de
                    acces și de securitate, cookie-urile descrise la secțiunea
                    9.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold text-white">
                      Date furnizate de utilizatori
                    </span>
                    : fotografii sau alte informații încărcate (ex. imagini ale
                    locațiilor sportive), mesajele transmise către suport.
                  </span>
                </li>
              </ul>

              <div className="mt-6 p-5 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Nu prelucrăm datele complete ale cardului
                    </h3>
                    <p className="mb-3">
                      Numărul complet al cardului, data expirării și codul CVV
                      sunt introduse direct în pagina securizată a
                      procesatorului de plăți Stripe și nu ajung niciodată pe
                      serverele Atlivo. Noi primim doar o referință tehnică ce
                      ne permite să inițiem plata abonamentului.
                    </p>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Despre CNP
                    </h3>
                    <p>
                      Pentru persoanele fizice, completarea CNP-ului este{" "}
                      <span className="font-semibold text-white">
                        opțională
                      </span>
                      . Dacă alegeți să nu îl furnizați, folosim pe factură codul
                      generic prevăzut de reglementările fiscale. Atunci când
                      este furnizat, CNP-ul este utilizat exclusiv pentru
                      emiterea și arhivarea facturii, în temeiul obligației
                      legale de facturare, nu în scopuri de marketing, de
                      profilare sau de identificare în Platformă.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section id="cum-colectam" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                4. Cum Colectăm Datele
              </h2>
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="bg-gray-800/70 p-5 rounded-lg border border-gray-700/70">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Direct de la dumneavoastră, atunci când:
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Vă creați un cont;</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Efectuați o rezervare;</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>
                        Completați datele de facturare sau porniți un abonament;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Completați formulare sau ne contactați;</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Încărcați conținut pe Platformă.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-800/70 p-5 rounded-lg border border-gray-700/70">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Din alte surse:
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>
                        Automat, prin cookie-uri și informații despre dispozitiv
                        și conexiune;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>
                        Din registrul public al ANAF, atunci când completăm
                        automat datele firmei pe baza CUI-ului introdus de
                        dumneavoastră (denumire, adresă, stare de înregistrare
                        în scopuri de TVA);
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>
                        De la procesatorul de plăți, care ne comunică rezultatul
                        tranzacțiilor și datele neidentificatoare ale cardului;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>
                        De la proprietarul locației sau personalul acestuia,
                        atunci când înregistrează o rezervare în numele
                        dumneavoastră;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>
                        De la Google, dacă alegeți autentificarea cu contul
                        Google (nume și adresă de e-mail).
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="scop" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                5. Scopul și Temeiul Prelucrării
              </h2>
              <p className="mb-4">
                Prelucrăm datele dumneavoastră numai în scopurile de mai jos și
                numai în temeiurile indicate:
              </p>
              <div className="space-y-3">
                {PURPOSES.map((item) => (
                  <div
                    key={item.purpose}
                    className="bg-gray-800/70 p-4 rounded-lg border border-gray-700/70"
                  >
                    <p className="text-white mb-1">{item.purpose}</p>
                    <p className="text-sm text-blue-300">{item.basis}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4">
                Atunci când prelucrarea se bazează pe consimțământ, îl puteți
                retrage oricând, fără a afecta legalitatea prelucrărilor
                anterioare.
              </p>
            </section>

            <section id="dezvaluire" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                6. Destinatarii Datelor
              </h2>
              <p className="mb-4">
                Nu vindem datele dumneavoastră. Le divulgăm doar în măsura
                necesară funcționării Platformei, către:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Proprietarii de locații sportive și personalul acestora,
                    pentru gestionarea rezervărilor;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Furnizorii de servicii care prelucrează date în numele
                    nostru, enumerați mai jos;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Autoritățile publice (inclusiv ANAF), atunci când legea ne
                    obligă;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Consultanți profesioniști (contabili, avocați, auditori),
                    sub obligație de confidențialitate.
                  </span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white mb-3">
                Furnizorii cu care lucrăm
              </h3>
              <div className="space-y-3">
                {RECIPIENTS.map((recipient) => (
                  <div
                    key={recipient.name}
                    className="bg-gray-800/70 p-4 rounded-lg border border-gray-700/70"
                  >
                    <div className="flex items-start gap-3">
                      <Server className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-white">
                          {recipient.name}
                        </p>
                        <p className="text-sm mb-1">{recipient.role}</p>
                        <p className="text-sm text-gray-400">
                          Locație: {recipient.location}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-400">
                Serviciul de autentificare al Platformei este operat de noi, pe
                propria infrastructură. Lista poate fi actualizată; modificările
                sunt reflectate în această politică.
              </p>
            </section>

            <section id="transfer" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                7. Transferul Datelor în Afara UE/SEE
              </h2>
              <div className="flex items-start gap-3 p-5 bg-gray-800/70 rounded-lg">
                <Globe className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-3">
                  <p>
                    Infrastructura principală a Platformei (aplicația, baza de
                    date și fotografiile încărcate) este găzduită în Uniunea
                    Europeană, în Germania.
                  </p>
                  <p>
                    Anumiți furnizori pot însă prelucra date în Statele Unite ale
                    Americii: procesatorul de plăți (Stripe), furnizorul de SMS
                    (Twilio) și furnizorul serviciului de e-mail (Porkbun). În
                    aceste cazuri, transferurile se realizează pe baza
                    garanțiilor prevăzute de capitolul V din GDPR — clauzele
                    contractuale standard aprobate de Comisia Europeană și/sau,
                    după caz, decizia de adecvare privind Cadrul de
                    Confidențialitate a Datelor UE–SUA — completate cu măsuri
                    tehnice suplimentare, precum criptarea datelor în tranzit.
                  </p>
                  <p>
                    Puteți solicita o copie a garanțiilor aplicabile scriindu-ne
                    la support@atlivo.com.
                  </p>
                </div>
              </div>
            </section>

            <section id="stocare" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                8. Perioada de Stocare
              </h2>
              <p className="mb-4">
                Păstrăm datele doar atât timp cât este necesar pentru scopurile
                descrise mai sus:
              </p>
              <div className="space-y-3">
                {RETENTION.map((item) => (
                  <div
                    key={item.what}
                    className="bg-gray-800/70 p-4 rounded-lg border border-gray-700/70"
                  >
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-white">{item.what}</p>
                        <p className="text-sm">{item.how_long}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4">
                La expirarea acestor termene, datele sunt șterse sau
                anonimizate. Copiile de siguranță se rotesc periodic, astfel
                încât datele șterse dispar și din acestea în cadrul ciclului
                normal de rotație.
              </p>
            </section>

            <section id="cookies" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                9. Cookie-uri și Tehnologii Similare
              </h2>
              <div className="flex items-start gap-3 p-5 bg-green-500/10 border border-green-500/20 rounded-lg mb-4">
                <Cookie className="w-6 h-6 text-green-400 mt-0.5 flex-shrink-0" />
                <p>
                  Atlivo folosește{" "}
                  <span className="font-semibold text-white">
                    doar cookie-uri strict necesare și funcționale
                  </span>
                  . Nu utilizăm cookie-uri de publicitate, de urmărire
                  comportamentală sau de analiză furnizate de terți, motiv
                  pentru care nu vă solicităm un consimțământ pentru cookie-uri.
                </p>
              </div>
              <div className="space-y-3">
                {COOKIES.map((cookie) => (
                  <div
                    key={cookie.name}
                    className="bg-gray-800/70 p-4 rounded-lg border border-gray-700/70"
                  >
                    <p className="font-semibold text-white mb-1">
                      {cookie.name}
                    </p>
                    <p className="text-sm mb-1">{cookie.purpose}</p>
                    <p className="text-sm text-gray-400">
                      Durată: {cookie.duration}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-4">
                Puteți bloca sau șterge cookie-urile din setările browserului,
                însă blocarea cookie-urilor de autentificare vă va împiedica să
                vă conectați la cont. Platforma încarcă hărți furnizate de HERE;
                la afișarea acestora, adresa dumneavoastră IP este comunicată
                furnizorului de hărți.
              </p>
            </section>

            <section id="drepturi" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                10. Drepturile Dumneavoastră
              </h2>
              <p className="mb-4">Conform GDPR, aveți dreptul de a:</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <p>Fi informat și de a accesa datele dumneavoastră</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <p>Rectifica datele inexacte sau incomplete</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <p>Solicita ștergerea datelor („dreptul de a fi uitat")</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <p>Restricționa prelucrarea</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <p>Vă opune prelucrării bazate pe interes legitim</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <p>Primi datele într-un format portabil</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-lg sm:col-span-2">
                  <p>
                    Retrage consimțământul, fără a afecta prelucrarea anterioară
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-3 p-4 bg-gray-800/70 rounded-lg">
                <UserCheck className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <p>
                  Pentru exercitarea acestor drepturi, ne puteți scrie la{" "}
                  <span className="text-blue-400">support@atlivo.com</span>.
                  Răspundem în cel mult o lună de la primirea cererii, termen ce
                  poate fi prelungit cu două luni pentru cereri complexe, cu
                  informarea dumneavoastră. Vă putem solicita informații
                  suplimentare pentru a vă confirma identitatea. Rețineți că
                  anumite date nu pot fi șterse la cerere atât timp cât avem
                  obligația legală de a le arhiva (de exemplu, facturile).
                </p>
              </div>

              <div className="mt-4 p-4 bg-gray-700/30 rounded-xl">
                <p className="font-semibold text-white mb-2">
                  Dreptul de a depune o plângere
                </p>
                <p>
                  Vă puteți adresa Autorității Naționale de Supraveghere a
                  Prelucrării Datelor cu Caracter Personal (ANSPDCP): B-dul
                  G-ral. Gheorghe Magheru nr. 28-30, Sector 1, cod poștal
                  010336, București, e-mail: anspdcp@dataprotection.ro,{" "}
                  <a
                    href="https://www.dataprotection.ro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors underline"
                  >
                    dataprotection.ro
                  </a>
                  . Vă puteți adresa, de asemenea, instanțelor de judecată
                  competente.
                </p>
              </div>
            </section>

            <section id="securitate" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                11. Securitatea Datelor
              </h2>
              <p className="mb-4">
                Adoptăm măsuri tehnice și organizatorice pentru a proteja
                datele, inclusiv:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Acces limitat la date, doar pentru personalul autorizat, pe
                    baza rolurilor;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Criptarea comunicațiilor (HTTPS) și securizarea serverelor;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Externalizarea completă a datelor de card către un procesator
                    de plăți certificat PCI DSS;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Jurnalizarea acțiunilor sensibile și limitarea numărului de
                    coduri SMS care pot fi solicitate;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Monitorizarea și actualizarea periodică a sistemelor de
                    securitate;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Încheierea de acorduri de prelucrare a datelor cu furnizorii
                    noștri.
                  </span>
                </li>
              </ul>
              <p className="mt-4">
                Nicio metodă de stocare nu este 100% sigură, dar lucrăm pentru a
                minimiza riscurile.
              </p>
            </section>

            <section id="brese" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                12. Breșele de Securitate
              </h2>
              <p>
                În caz de breșă de securitate care prezintă un risc pentru
                drepturile dumneavoastră, vom notifica ANSPDCP în maximum 72 de
                ore de la luarea la cunoștință și vom informa fără întârziere
                nejustificată utilizatorii afectați, atunci când riscul este
                ridicat, conform art. 33 și 34 din GDPR.
              </p>
            </section>

            <section id="decizii-automate" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                13. Decizii Automate și Profilare
              </h2>
              <p>
                Nu luăm decizii bazate exclusiv pe prelucrarea automată care să
                producă efecte juridice asupra dumneavoastră sau să vă afecteze
                în mod similar semnificativ și nu vă creăm profiluri de
                comportament. Procesele automate pe care le folosim — precum
                reînnoirea lunară a abonamentului, calcularea TVA-ului aplicabil
                sau limitarea numărului de coduri SMS — execută reguli
                prestabilite, comunicate în Termeni și Condiții, iar rezultatul
                lor poate fi oricând verificat de un membru al echipei noastre,
                la cerere.
              </p>
            </section>

            <section id="minori" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                14. Datele Minorilor
              </h2>
              <p>
                Platforma se adresează persoanelor care au împlinit 18 ani. Nu
                colectăm cu bună știință date ale minorilor și nu le solicităm
                crearea unui cont. Rezervările pentru minori se efectuează de
                către un părinte, tutore sau reprezentant al acestora, prin
                contul persoanei adulte. Dacă aflăm că am colectat date ale unui
                minor fără un temei corespunzător, le ștergem fără întârziere;
                ne puteți sesiza la support@atlivo.com.
              </p>
            </section>

            <section id="modificari" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                15. Modificări ale Politicii
              </h2>
              <p>
                Ne rezervăm dreptul de a actualiza această politică. Fiecare
                versiune poartă un număr și o dată a ultimei actualizări.
                Modificările vor fi publicate pe Platformă și, atunci când sunt
                substanțiale, vă vor fi comunicate prin e-mail cu cel puțin 30
                de zile înainte de intrarea lor în vigoare. Versiunile
                anterioare vor fi arhivate și disponibile la cerere.
              </p>
            </section>

            <section id="contact" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                16. Informații de Contact
              </h2>
              <div className="bg-gray-700/30 p-6 rounded-xl">
                <p className="mb-4">
                  Pentru întrebări privind prelucrarea datelor sau pentru
                  exercitarea drepturilor dumneavoastră, contactați-ne la:
                </p>
                <ul className="space-y-2">
                  <li>
                    <span className="font-semibold text-white">Operator:</span>{" "}
                    SEERSTECH SRL
                  </li>
                  <li>
                    <span className="font-semibold text-white">Sediu:</span>{" "}
                    Județul Sălaj, Municipiul Zalău, Strada Avram Iancu, Nr. 39,
                    Bloc N7, Ap. 7
                  </li>
                  <li>
                    <span className="font-semibold text-white">E-mail:</span>{" "}
                    support@atlivo.com
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyView;
